import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';

const AuditoriaManagement = () => {
  const { user } = useAuth();
  const [auditoria, setAuditoria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    modulo: '',
    accion: '',
    usuario: '',
    fechaInicio: '',
    fechaFin: ''
  });
  const [reporte, setReporte] = useState(null);
  const [showReporte, setShowReporte] = useState(false);

  useEffect(() => {
    if (user?.role === 'super-admin') {
      fetchAuditoria();
    }
  }, [filtros, user]);

  const fetchAuditoria = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filtros.modulo) params.append('modulo', filtros.modulo);
      if (filtros.accion) params.append('accion', filtros.accion);
      if (filtros.usuario) params.append('usuario', filtros.usuario);
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);

      const response = await axios.get(`/api/auditoria?${params.toString()}`);
      setAuditoria(response.data.data || []);
    } catch (error) {
      console.error('Error fetching auditoría:', error);
      toast.error('Error al cargar auditoría');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerarReporte = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filtros.modulo) params.append('modulo', filtros.modulo);
      if (filtros.fechaInicio) params.append('fechaInicio', filtros.fechaInicio);
      if (filtros.fechaFin) params.append('fechaFin', filtros.fechaFin);

      const response = await axios.get(`/api/auditoria/reporte?${params.toString()}`);
      setReporte(response.data.data);
      setShowReporte(true);
      toast.success('Reporte generado exitosamente');
    } catch (error) {
      console.error('Error al generar reporte:', error);
      toast.error('Error al generar reporte');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      modulo: '',
      accion: '',
      usuario: '',
      fechaInicio: '',
      fechaFin: ''
    });
  };

  const getAccionColor = (accion) => {
    const colores = {
      'CREATE': 'success',
      'READ': 'info',
      'UPDATE': 'warning',
      'DELETE': 'danger',
      'LOCK': 'secondary',
      'UNLOCK': 'secondary',
      'APPROVE': 'success',
      'REJECT': 'danger'
    };
    return colores[accion] || 'secondary';
  };

  const getEstadoColor = (estado) => {
    const colores = {
      'exitosa': 'success',
      'fallida': 'danger',
      'advertencia': 'warning'
    };
    return colores[estado] || 'secondary';
  };

  const formatFecha = (fecha) => {
    return new Date(fecha).toLocaleString('es-ES');
  };

  if (user?.role !== 'super-admin') {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <i className="fas fa-lock me-2"></i>
          Solo Super Administradores pueden acceder a la auditoría
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <h2 className="text-gradient mb-2">
                  <i className="fas fa-history me-2"></i>
                  Auditoría del Sistema
                </h2>
                <p className="text-muted mb-0">
                  Registro completo de todas las acciones realizadas en el sistema
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <Row>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Módulo</Form.Label>
                      <Form.Select
                        name="modulo"
                        value={filtros.modulo}
                        onChange={handleFiltroChange}
                      >
                        <option value="">Todos</option>
                        <option value="usuarios">Usuarios</option>
                        <option value="socios">Socios</option>
                        <option value="valores">Valores</option>
                        <option value="asistencia">Asistencia</option>
                        <option value="prestamos">Préstamos</option>
                        <option value="reportes">Reportes</option>
                        <option value="sistema">Sistema</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Acción</Form.Label>
                      <Form.Select
                        name="accion"
                        value={filtros.accion}
                        onChange={handleFiltroChange}
                      >
                        <option value="">Todas</option>
                        <option value="CREATE">Crear</option>
                        <option value="READ">Leer</option>
                        <option value="UPDATE">Actualizar</option>
                        <option value="DELETE">Eliminar</option>
                        <option value="LOCK">Bloquear</option>
                        <option value="UNLOCK">Desbloquear</option>
                        <option value="APPROVE">Aprobar</option>
                        <option value="REJECT">Rechazar</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Desde</Form.Label>
                      <Form.Control
                        type="date"
                        name="fechaInicio"
                        value={filtros.fechaInicio}
                        onChange={handleFiltroChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={2}>
                    <Form.Group>
                      <Form.Label>Hasta</Form.Label>
                      <Form.Control
                        type="date"
                        name="fechaFin"
                        value={filtros.fechaFin}
                        onChange={handleFiltroChange}
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4} className="d-flex align-items-end gap-2">
                    <Button
                      variant="outline-secondary"
                      onClick={limpiarFiltros}
                      className="flex-fill"
                    >
                      <i className="fas fa-times me-1"></i>
                      Limpiar
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleGenerarReporte}
                      disabled={loading}
                      className="flex-fill"
                    >
                      <i className="fas fa-chart-bar me-1"></i>
                      Reporte
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Tabla de Auditoría */}
        <Row>
          <Col>
            <Card className="card-custom">
              <Card.Header className="card-header-custom">
                <h5 className="mb-0">
                  <i className="fas fa-table me-2"></i>
                  Historial de Acciones
                </h5>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </Spinner>
                  </div>
                ) : auditoria.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="fas fa-inbox fa-3x mb-3 d-block"></i>
                    No hay registros de auditoría
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover>
                      <thead>
                        <tr>
                          <th>Fecha</th>
                          <th>Usuario</th>
                          <th>Módulo</th>
                          <th>Acción</th>
                          <th>Descripción</th>
                          <th>Estado</th>
                          <th>IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditoria.map((registro, index) => (
                          <tr key={index}>
                            <td className="small">{formatFecha(registro.fecha)}</td>
                            <td>
                              <strong>{registro.usuario?.name || 'Desconocido'}</strong>
                              <br />
                              <small className="text-muted">{registro.usuario?.email}</small>
                            </td>
                            <td>
                              <Badge bg="info">{registro.modulo}</Badge>
                            </td>
                            <td>
                              <Badge bg={getAccionColor(registro.accion)}>
                                {registro.accion}
                              </Badge>
                            </td>
                            <td>{registro.descripcion}</td>
                            <td>
                              <Badge bg={getEstadoColor(registro.estado)}>
                                {registro.estado}
                              </Badge>
                            </td>
                            <td className="small text-muted">{registro.direccionIP}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Reporte */}
        {showReporte && reporte && (
          <Row className="mt-4">
            <Col>
              <Card className="card-custom">
                <Card.Header className="card-header-custom">
                  <h5 className="mb-0">
                    <i className="fas fa-chart-bar me-2"></i>
                    Reporte de Auditoría
                  </h5>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-4">
                    <Col md={3}>
                      <Card className="bg-light">
                        <Card.Body>
                          <small className="text-muted">Total de Registros</small>
                          <h3 className="text-primary mb-0">{reporte.totalRegistros}</h3>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={3}>
                      <Card className="bg-light">
                        <Card.Body>
                          <small className="text-muted">Período</small>
                          <h6 className="mb-0">
                            {reporte.periodo.inicio} a {reporte.periodo.fin}
                          </h6>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  <Row className="mb-4">
                    <Col md={6}>
                      <h6 className="mb-3">
                        <i className="fas fa-cube me-2"></i>
                        Acciones por Módulo
                      </h6>
                      {Object.entries(reporte.porModulo).map(([modulo, datos]) => (
                        <div key={modulo} className="mb-3 p-3 bg-light rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>{modulo}</strong>
                            <Badge bg="primary">{datos.total}</Badge>
                          </div>
                          <div className="small">
                            {Object.entries(datos.acciones).map(([accion, cantidad]) => (
                              <div key={accion} className="d-flex justify-content-between">
                                <span>{accion}:</span>
                                <strong>{cantidad}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </Col>

                    <Col md={6}>
                      <h6 className="mb-3">
                        <i className="fas fa-user me-2"></i>
                        Acciones por Usuario
                      </h6>
                      {Object.entries(reporte.porUsuario).map(([usuario, datos]) => (
                        <div key={usuario} className="mb-3 p-3 bg-light rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>{usuario}</strong>
                            <Badge bg="success">{datos.total}</Badge>
                          </div>
                          <div className="small">
                            {Object.entries(datos.acciones).map(([accion, cantidad]) => (
                              <div key={accion} className="d-flex justify-content-between">
                                <span>{accion}:</span>
                                <strong>{cantidad}</strong>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </Container>
  );
};

export default AuditoriaManagement;
